// Детальная страница проекта (ПСД): PDF, замечания, workflow статусов.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileText, Send, Check, X, MessageSquarePlus, Trash2, ExternalLink,
} from 'lucide-react';
import { projectsApi, getErrorMessage } from '@/api';
import { AppLayout } from '@/components/layout/AppLayout';
import { Spinner } from '@/components/ui/Spinner';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/hooks/useAuth';
import { formatDate } from '@/lib/format';

const STATUS_COLOR: Record<string, string> = {
  draft: 'var(--text-muted)', review: 'var(--yellow)',
  approved: 'var(--green)', rejected: 'var(--red)',
};

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const projectId = Number(id);
  const navigate = useNavigate();
  const { can } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [newNote, setNewNote] = useState('');
  const [notePage, setNotePage] = useState(1);

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['project', projectId] });
    qc.invalidateQueries({ queryKey: ['projects'] });
    qc.invalidateQueries({ queryKey: ['nav-counts'] });
  };

  const submitM = useMutation({ mutationFn: () => projectsApi.submit(projectId),
    onSuccess: () => { invalidate(); toast('Отправлено на проверку', 'success'); },
    onError: (e) => toast(getErrorMessage(e), 'error') });
  const approveM = useMutation({ mutationFn: () => projectsApi.approve(projectId),
    onSuccess: () => { invalidate(); toast('Проект утверждён', 'success'); },
    onError: (e) => toast(getErrorMessage(e), 'error') });
  const rejectM = useMutation({ mutationFn: () => projectsApi.reject(projectId),
    onSuccess: () => { invalidate(); toast('Проект отклонён', 'success'); },
    onError: (e) => toast(getErrorMessage(e), 'error') });

  const addNoteM = useMutation({
    mutationFn: () => projectsApi.addAnnotation({ project: projectId, page: notePage, x: 0, y: 0, text: newNote.trim() }),
    onSuccess: () => { setNewNote(''); invalidate(); toast('Замечание добавлено', 'success'); },
    onError: (e) => toast(getErrorMessage(e), 'error'),
  });
  const delNoteM = useMutation({
    mutationFn: (aid: number) => projectsApi.deleteAnnotation(aid),
    onSuccess: () => { invalidate(); },
    onError: (e) => toast(getErrorMessage(e), 'error'),
  });

  if (isLoading) return <AppLayout><Spinner label="Загрузка проекта…" /></AppLayout>;
  if (!project) return <AppLayout><EmptyState title="Проект не найден" /></AppLayout>;

  const canReview = can('projects', 'change');

  return (
    <AppLayout>
      <button className="link-back" onClick={() => navigate('/projects')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--text-secondary)', marginBottom: 16, cursor: 'pointer' }}>
        <ArrowLeft size={16} /> К списку проектов
      </button>

      {/* Шапка проекта + действия workflow */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: 'var(--font-display)', fontSize: 28 }}>
            <FileText size={24} style={{ color: 'var(--accent)' }} />{project.number}
          </h1>
          <div style={{ color: 'var(--text-secondary)', marginTop: 6, display: 'flex', gap: 16, alignItems: 'center' }}>
            <span>Договор: {project.contract_number}</span>
            <span>Страниц: {project.pages_count}</span>
            <StatusBadge label={project.status_display} color={STATUS_COLOR[project.status]} />
          </div>
        </div>
        {canReview && (
          <div style={{ display: 'flex', gap: 8 }}>
            {project.status === 'draft' && (
              <Button variant="primary" disabled={submitM.isPending} onClick={() => submitM.mutate()}>
                <Send size={15} /> На проверку
              </Button>
            )}
            {project.status === 'review' && (
              <>
                <Button variant="primary" disabled={approveM.isPending} onClick={() => approveM.mutate()}>
                  <Check size={15} /> Утвердить
                </Button>
                <Button variant="danger" disabled={rejectM.isPending} onClick={() => rejectM.mutate()}>
                  <X size={15} /> Отклонить
                </Button>
              </>
            )}
            {(project.status === 'approved' || project.status === 'rejected') && (
              <Button disabled={submitM.isPending} onClick={() => submitM.mutate()}>
                <Send size={15} /> Вернуть на проверку
              </Button>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 20, alignItems: 'start' }}>
        {/* PDF */}
        <div className="panel" style={{ padding: 0, overflow: 'hidden', minHeight: 480 }}>
          {project.pdf_file ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Проектная документация</span>
                <a href={project.pdf_file} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--accent)', fontSize: 13 }}>
                  Открыть в новой вкладке <ExternalLink size={13} />
                </a>
              </div>
              <iframe src={project.pdf_file} title="PDF проекта" style={{ width: '100%', height: 600, border: 'none', background: '#fff' }} />
            </>
          ) : (
            <div style={{ padding: 48 }}>
              <EmptyState icon={<FileText size={32} />} title="PDF не загружен"
                hint="Документ можно прикрепить при редактировании проекта" />
            </div>
          )}
        </div>

        {/* Замечания (аннотации) */}
        <div className="panel">
          <h3 style={{ marginBottom: 14, fontSize: 16 }}>Замечания ({project.annotations.length})</h3>

          {canReview && (
            <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <label style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Стр.</label>
                <input type="number" min={1} value={notePage} className="field__input" style={{ width: 70 }}
                  onChange={(e) => setNotePage(Math.max(1, Number(e.target.value)))} />
              </div>
              <textarea className="field__textarea" rows={3} value={newNote}
                onChange={(e) => setNewNote(e.target.value)} placeholder="Текст замечания…" />
              <Button variant="primary" disabled={!newNote.trim() || addNoteM.isPending} onClick={() => addNoteM.mutate()}>
                <MessageSquarePlus size={15} /> Добавить замечание
              </Button>
            </div>
          )}

          {project.annotations.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Замечаний пока нет</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {project.annotations.map((a) => (
                <div key={a.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>стр. {a.page}</span>
                    {canReview && (
                      <button onClick={() => delNoteM.mutate(a.id)} title="Удалить"
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{a.text}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                    {a.author_name || '—'} · {formatDate(a.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
